import { useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion, useReducedMotion } from 'framer-motion';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Calendar as CalendarIcon, Plus, Trash2, X } from 'lucide-react';
import { eventInputSchema, type EventInput } from '@underline/shared';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { FieldShell, StackedField } from '@/components/ui/stacked-field';
import { DatePicker } from '@/components/ui/date-picker';
import { TimePicker } from '@/components/ui/time-picker';
import { AttractionAutocomplete } from '@/components/attractions/attraction-autocomplete';
import { PromoEditor } from '@/components/events/promo-editor';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { CurrencyInput } from '@/components/ui/currency-input';
import { useCreateEvent, useDeleteEvent, useUpdateEvent } from '@/hooks/use-events';
import { useAuthStore } from '@/stores/auth-store';
import { useUiStore } from '@/stores/ui-store';
import { todayISO } from '@/lib/dates';
import { ease } from '@/lib/motion';

/* ── Tipografia MONO 12px — hierarquia só por peso/cor ──────────────── */
const TITLE_MONO = 'font-mono text-[12px] font-semibold uppercase tracking-wider text-foreground';
const SECTION_MONO = 'font-mono text-[12px] uppercase tracking-wider text-muted-foreground';
const FIELD_MONO = 'mono uppercase tabular-nums text-[12px]';
const BTN_MONO = 'font-mono text-[12px] uppercase tracking-wider';

// Couvert: Noite toda / Até o horário / Pago — mapeados p/ free / free_until / flat.
const couvertOptions = [
  { value: 'free' as const, label: 'Noite toda' },
  { value: 'free_until' as const, label: 'Até horário' },
  { value: 'flat' as const, label: 'Pago' },
];

export function EventFormModal() {
  const open = useUiStore((s) => s.eventFormOpen);
  const close = useUiStore((s) => s.closeEventForm);
  const editing = useUiStore((s) => s.editingEvent);
  const user = useAuthStore((s) => s.user);
  const canDelete = user?.role === 'admin' || user?.role === 'gerente';
  const reduce = useReducedMotion();

  const create = useCreateEvent();
  const update = useUpdateEvent();
  const del = useDeleteEvent();

  const isEdit = !!editing;

  // Passo do wizard (1 = dados · 2 = line-up). Direção alimenta a animação.
  const [step, setStep] = useState<1 | 2>(1);
  const [dir, setDir] = useState(0);

  const form = useForm<EventInput>({
    resolver: zodResolver(eventInputSchema),
    shouldUnregister: false,
    defaultValues: editing
      ? {
          brand_id: editing.brand_id,
          name: editing.name,
          event_date: editing.event_date.slice(0, 10),
          open_time: editing.open_time ? editing.open_time.slice(0, 5) : null,
          close_time: editing.close_time ? editing.close_time.slice(0, 5) : null,
          attractions: editing.attractions.length
            ? editing.attractions.map((a) => ({
                ...a,
                time: a.time ? a.time.slice(0, 5) : null,
              }))
            : [{ attraction_id: null, name: '', time: null }],
          djs: editing.djs,
          couvert_type: editing.couvert_type,
          couvert_value: editing.couvert_value != null ? Number(editing.couvert_value) : null,
          couvert_until: editing.couvert_until ? editing.couvert_until.slice(0, 5) : null,
          highlight: editing.highlight ?? null,
          promos: editing.promos,
          cover_image_url: editing.cover_image_url,
          status: editing.status,
        }
      : {
          brand_id: 1,
          name: '',
          event_date: todayISO(),
          open_time: '22:00',
          close_time: '04:00',
          attractions: [{ attraction_id: null, name: '', time: null }],
          djs: null,
          couvert_type: 'free',
          couvert_value: null,
          couvert_until: null,
          highlight: null,
          promos: null,
          cover_image_url: null,
          status: 'published',
        },
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    trigger,
    formState: { errors, isSubmitting },
  } = form;

  const { fields, append, remove } = useFieldArray({ control, name: 'attractions' });
  const couvertType = watch('couvert_type');

  async function goNext() {
    const ok = await trigger(['name', 'event_date', 'open_time', 'close_time']);
    if (!ok) {
      const first = Object.values(errors)[0] as { message?: string } | undefined;
      toast.error(first?.message ?? 'Verifique os campos obrigatórios');
      return;
    }
    setDir(1);
    setStep(2);
  }

  function goBack() {
    setDir(-1);
    setStep(1);
  }

  async function onSubmit(values: EventInput) {
    if (step !== 2) return; // submit só vale no passo final
    const cleaned = {
      ...values,
      attractions: values.attractions.filter((a) => a.name.trim().length > 0),
      djs: null,
    };
    try {
      if (isEdit && editing) {
        await update.mutateAsync({ id: editing.id, input: cleaned });
        toast.success('Evento atualizado');
      } else {
        await create.mutateAsync(cleaned);
        toast.success('Evento criado');
      }
      close();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao salvar';
      toast.error(msg);
    }
  }

  async function handleDelete() {
    if (!editing) return;
    if (!confirm('Excluir esse evento?')) return;
    try {
      await del.mutateAsync(editing.id);
      toast.success('Evento excluído');
      close();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao excluir';
      toast.error(msg);
    }
  }

  const title =
    step === 1 ? (isEdit ? 'Editar evento' : 'Novo evento') : 'Line-up e atrativos';

  return (
    <BottomSheet
      open={open}
      onClose={close}
      title={title}
      titleClassName={TITLE_MONO}
      ariaLabel={title}
      leading={
        step === 2 ? (
          <button
            type="button"
            onClick={goBack}
            aria-label="Voltar"
            className="grid h-8 w-8 place-items-center rounded-full bg-surface-2 text-muted-foreground transition active:scale-[0.97] hover:bg-muted"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        ) : undefined
      }
    >
      <p className={`${SECTION_MONO} -mt-1 mb-3`}>
        {step === 1 ? 'Etapa 1 de 2 · Dados' : 'Etapa 2 de 2 · Line-up'}
      </p>

      <form
        onSubmit={handleSubmit(onSubmit, (errs) => {
          const first = Object.values(errs)[0] as { message?: string } | undefined;
          toast.error(first?.message ?? 'Verifique os campos obrigatórios');
        })}
        className="flex flex-col gap-[18px]"
      >
        <motion.div
          key={step}
          initial={reduce || dir === 0 ? false : { opacity: 0, x: dir > 0 ? 16 : -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.22, ease }}
          className="flex flex-col gap-[18px]"
        >
          {step === 1 ? (
            /* ─────────────── PASSO 1 · DADOS DO EVENTO ─────────────── */
            <>
              <StackedField
                label="Nome do evento"
                placeholder="UNDERLINE SESSIONS: VORAH"
                error={errors.name?.message}
                autoFocus
                className={FIELD_MONO}
                {...register('name')}
              />

              <FieldShell label="Data do evento" error={errors.event_date?.message}>
                <Controller
                  control={control}
                  name="event_date"
                  render={({ field }) => (
                    <DatePicker value={field.value} onChange={field.onChange} />
                  )}
                />
              </FieldShell>

              <div className="grid grid-cols-2 gap-3">
                <FieldShell label="Abre">
                  <Controller
                    control={control}
                    name="open_time"
                    render={({ field }) => (
                      <TimePicker value={field.value} onChange={field.onChange} />
                    )}
                  />
                </FieldShell>
                <FieldShell label="Fecha">
                  <Controller
                    control={control}
                    name="close_time"
                    render={({ field }) => (
                      <TimePicker value={field.value} onChange={field.onChange} />
                    )}
                  />
                </FieldShell>
              </div>

              <div>
                <p className={`${SECTION_MONO} mb-2`}>Couvert free?</p>
                <SegmentedControl
                  value={couvertType}
                  onChange={(v) => setValue('couvert_type', v, { shouldDirty: true })}
                  options={couvertOptions}
                  size="sm"
                  className="font-mono uppercase"
                />
                {couvertType === 'free_until' && (
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <FieldShell label="Free até">
                      <Controller
                        control={control}
                        name="couvert_until"
                        render={({ field }) => (
                          <TimePicker value={field.value} onChange={field.onChange} />
                        )}
                      />
                    </FieldShell>
                    <Controller
                      control={control}
                      name="couvert_value"
                      render={({ field }) => (
                        <CurrencyInput
                          label="Valor após"
                          value={field.value}
                          onChange={(v) => field.onChange(v)}
                        />
                      )}
                    />
                  </div>
                )}
                {couvertType === 'flat' && (
                  <div className="mt-3">
                    <Controller
                      control={control}
                      name="couvert_value"
                      render={({ field }) => (
                        <CurrencyInput
                          label="Valor R$"
                          value={field.value}
                          onChange={(v) => field.onChange(v)}
                        />
                      )}
                    />
                  </div>
                )}
              </div>

              <div className="pt-2">
                <button
                  type="button"
                  onClick={goNext}
                  className={`flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 font-semibold text-primary-foreground shadow-red transition active:scale-[0.97] ${BTN_MONO}`}
                >
                  Continuar
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </>
          ) : (
            /* ─────────────── PASSO 2 · LINE-UP & ATRATIVOS ─────────────── */
            <>
              <div>
                <p className={`${SECTION_MONO} mb-2`}>Line-up</p>
                <div className="flex flex-col gap-3">
                  {fields.map((f, i) => {
                    const isLast = i === fields.length - 1;
                    return (
                      <div key={f.id}>
                        <p className={`${SECTION_MONO} mb-1.5`}>
                          DJ {String(i + 1).padStart(2, '0')}
                        </p>
                        <div className="flex items-stretch gap-2">
                          <div className="min-w-0 flex-1">
                            <Controller
                              control={control}
                              name={`attractions.${i}.name` as const}
                              render={({ field }) => (
                                <AttractionAutocomplete
                                  value={field.value}
                                  onChange={(name, attractionId) => {
                                    field.onChange(name);
                                    setValue(
                                      `attractions.${i}.attraction_id`,
                                      attractionId ?? null,
                                      { shouldDirty: true },
                                    );
                                  }}
                                  placeholder="DJ / Banda"
                                />
                              )}
                            />
                          </div>
                          <div className="w-[104px] shrink-0">
                            <Controller
                              control={control}
                              name={`attractions.${i}.time` as const}
                              render={({ field }) => (
                                <TimePicker value={field.value ?? null} onChange={field.onChange} />
                              )}
                            />
                          </div>
                          {isLast ? (
                            <button
                              type="button"
                              onClick={() => append({ attraction_id: null, name: '', time: null })}
                              aria-label="Adicionar atração"
                              className="grid w-[46px] shrink-0 place-items-center rounded-lg border border-border bg-card text-primary transition active:scale-[0.97] hover:bg-muted"
                            >
                              <Plus className="h-4 w-4" strokeWidth={2.5} />
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => remove(i)}
                              aria-label="Remover atração"
                              className="grid w-[46px] shrink-0 place-items-center rounded-lg border border-border bg-card text-muted-foreground transition active:scale-[0.97] hover:text-destructive"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <StackedField
                label="Destaque"
                placeholder="FRASE OU ALGO QUE TERÁ DESTAQUE NESSE DIA"
                maxLength={160}
                className={FIELD_MONO}
                {...register('highlight', { setValueAs: (v: string) => v?.trim() || null })}
              />

              <Controller
                control={control}
                name="promos"
                render={({ field }) => (
                  <PromoEditor
                    value={field.value ?? null}
                    onChange={(v) => field.onChange(v)}
                  />
                )}
              />

              <div className="flex flex-wrap items-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={close}
                  className={`flex-1 rounded-lg border border-border bg-card px-4 py-3 hover:bg-muted ${BTN_MONO} font-medium`}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`flex-[2] flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 font-semibold text-primary-foreground shadow-red transition active:scale-[0.97] disabled:opacity-60 ${BTN_MONO}`}
                >
                  <CalendarIcon className="h-4 w-4" />
                  {isSubmitting ? 'Salvando...' : isEdit ? 'Salvar' : 'Criar evento'}
                </button>
                {isEdit && canDelete && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    className={`mt-2 inline-flex w-full items-center justify-center gap-1 text-destructive ${BTN_MONO} font-medium`}
                  >
                    <Trash2 className="h-3 w-3" /> Excluir evento
                  </button>
                )}
              </div>
            </>
          )}
        </motion.div>
      </form>
    </BottomSheet>
  );
}
